'use client';

import { Link } from '@tanstack/react-router';
import { Building2, KeyRound, Plus, Router } from 'lucide-react';
import { Badge, Button, cn, TableCell, TableRow } from '@/components/ui';
import type { Partner } from '@/lib/types';

interface PartnerTreeRowsProps {
  partners: Partner[];
  parentId: string | null;
  depth: number;
  onAddChild: (parentId: string) => void;
}

/**
 * One level of the tree, then itself for the next.
 *
 * Rendered as table rows with an indent rather than nested tables, so the
 * columns stay aligned however deep the chain goes.
 */
export function PartnerTreeRows({ partners, parentId, depth, onAddChild }: PartnerTreeRowsProps) {
  const level = partners.filter((partner) => partner.parentId === parentId);

  return (
    <>
      {level.map((partner) => (
        <PartnerRowGroup
          key={partner.id}
          partner={partner}
          partners={partners}
          depth={depth}
          onAddChild={onAddChild}
        />
      ))}
    </>
  );
}

function PartnerRowGroup({
  partner,
  partners,
  depth,
  onAddChild,
}: {
  partner: Partner;
  partners: Partner[];
  depth: number;
  onAddChild: (parentId: string) => void;
}) {
  const stats = partner.stats;

  return (
    <>
      <TableRow className={cn(!partner.isActive && 'opacity-60')}>
        <TableCell>
          <div className="flex items-center gap-3" style={{ paddingLeft: `${depth * 24}px` }}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Router className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <Link
                to="/partners/$partnerId"
                params={{ partnerId: partner.id }}
                className="font-medium hover:underline"
              >
                {partner.name}
              </Link>
              <p className="text-sm text-muted-foreground">
                {depth === 0 ? 'Integrator' : 'Sub-dealer'} · {partner.slug}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <TokenCell partner={partner} inherited={depth > 0} />
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {stats ? `${stats.allocated} of ${stats.devices} allocated` : '—'}
        </TableCell>
        <TableCell>
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4" />
            {stats?.organizations ?? 0}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <Button variant="ghost" size="sm" onClick={() => onAddChild(partner.id)}>
            <Plus className="mr-1 h-4 w-4" />
            Sub-dealer
          </Button>
        </TableCell>
      </TableRow>

      <PartnerTreeRows
        partners={partners}
        parentId={partner.id}
        depth={depth + 1}
        onAddChild={onAddChild}
      />
    </>
  );
}

/** A token is either stored here, inherited from above, or missing. */
function TokenCell({ partner, inherited }: { partner: Partner; inherited: boolean }) {
  if (!partner.credential) {
    return <Badge variant="secondary">{inherited ? 'Inherited' : 'None'}</Badge>;
  }

  const failed = partner.credential.lastCheckOk === false;

  return (
    <span className="flex items-center gap-2">
      <KeyRound className="h-4 w-4 text-muted-foreground" />
      <code className="text-xs text-muted-foreground">{partner.credential.hint}</code>
      <Badge variant={failed ? 'destructive' : 'success'}>{failed ? 'Failing' : 'Stored'}</Badge>
    </span>
  );
}
